import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { SearchQueryBody } from "@/types";

import {
  type QueryContentMode,
  type QueryRecord,
  type QueryTarget,
} from "../QueryRecord";
import type { QueryService } from "../QueryService";

export interface UseStructuredQueryOptions {
  resolvedOrgId: string | undefined;
  /**
   * The full query body, including any `max_results` cap. Callers bake the cap
   * into the body (e.g. via `composeQueryBody({ maxResults })`); the hook submits
   * the body verbatim.
   */
  query: SearchQueryBody;
  target: QueryTarget;
  contentMode: QueryContentMode;
  /**
   * Caller-supplied cache key (e.g., `datasetQueryKeys.list(orgId, query)`)
   * so submit cache aligns with mutation-side invalidation.
   */
  queryKey: readonly unknown[];
  /**
   * How long a submission counts as fresh. Defaults to the app's. `Infinity`
   * keeps one result set for as long as it is cached: returning to the tab
   * does not submit the query again.
   */
  staleTime?: number;
}

/**
 * Query options for submitting a structured query. Extracted from the hook so
 * code outside render can await the same submission through
 * `queryClient.fetchQuery`, sharing the hook's cache entry instead of
 * submitting twice.
 */
export function structuredQueryOptions(
  queryService: QueryService,
  {
    resolvedOrgId,
    query,
    target,
    contentMode,
    queryKey,
    staleTime,
  }: UseStructuredQueryOptions,
) {
  return queryOptions({
    queryKey,
    queryFn: ({ signal }) =>
      queryService.submitStructured(
        {
          query,
          target,
          content_mode: contentMode,
        },
        { resourceOwnerId: resolvedOrgId, abortSignal: signal },
      ),
    enabled: !!resolvedOrgId,
    // Only when given: an explicit `undefined` would override the app's
    // default rather than fall back to it.
    ...(staleTime !== undefined && { staleTime }),
  });
}

export function useStructuredQuery(
  opts: UseStructuredQueryOptions,
): UseQueryResult<QueryRecord, Error> {
  const { queryService } = useDomainServices();

  return useQuery({
    ...structuredQueryOptions(queryService, opts),
    placeholderData: keepPreviousData,
  });
}
